import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { CreateCustomsProductRequest, CustomsProduct, RefineCalculationRequest, CertificateChecklist } from './customs-calculation';
import { PageResponse } from './page-response';
import { FileDto } from './file';
import { CargoRequest } from './cargo-request';

// Re-export для удобства использования
export type { CreateCustomsProductRequest, CustomsProduct, FollowUpQuestion, FollowUpOption, RefineCalculationRequest, CertificateChecklist, RequiredDocument } from './customs-calculation';

const API_URL = '/cargo/api/customs-calculations';
const INVOICE_API_URL = '/cargo/api/customs-calculation-invoices';

export interface SearchCustomsCalculationsRequest {
  dateFrom?: string; // YYYY-MM-DD
  dateTo?: string; // YYYY-MM-DD
  title?: string;
  status?: 'CREATED' | 'IN_PROCESSING' | 'PROCESSED';
}

export interface CreateCustomsCalculationRequest {
  title?: string;
}

export interface CustomsCalculation {
  id: number;
  /** Заявки на перевозку, созданные из этого расчёта — может быть несколько (сравнение
   *  стоимости перевозки); сделка в итоге только по одной (см. dealId внутри элемента). */
  cargoRequests?: CargoRequest[];
  userId?: number;
  ownerDisplayName?: string;
  title?: string;
  brokerComment?: string;
  products: CustomsProduct[];
  calculationStarted?: boolean;
  calculationStartedByUserId?: number;
  calculationStartedAt?: string;
  calculationCompleted?: boolean;
  calculationCompletedByUserId?: number;
  calculationCompletedAt?: string;
  completedWithAi?: boolean;
  /** CREATED / IN_PROCESSING / PROCESSED */
  status?: 'CREATED' | 'IN_PROCESSING' | 'PROCESSED';
  createdAt?: string;
  updatedAt?: string;

  /** ID поставщика, привязанного к этой перевозке (вкладка «Поставка»); нет, пока приглашение не принято */
  supplierId?: number;
  /** Название компании поставщика, привязанного к этой перевозке */
  supplierCompanyName?: string;

  // ─── Информация о поставке ─────────────────────────────────────────────
  fromCountryId?: number;
  fromCountryName?: string;
  fromCity?: string;
  toCountryId?: number;
  toCountryName?: string;
  toCity?: string;
  incotermCode?: string;
  /** true — incotermCode подставлен автоматически из распознанного PDF-инвойса, а не выбран пользователем */
  incotermFromInvoice?: boolean;
  totalWeightKg?: number;
  totalVolumeM3?: number;
  transportType?: 'FTL' | 'LTL' | 'AIR' | 'CONTAINER';
  freightEnabled?: boolean;
  /** Товар доставляет продавец: true или null — доставку к таможенной стоимости не добавляем; false — добавляем (80%, авиа 100%). */
  sellerDelivers?: boolean | null;
  /** Стоимость доставки ДО границы (аэропорта) — включается в таможенную стоимость */
  freightAmount?: number;
  /** Стоимость доставки ПОСЛЕ границы — информационно, не включается в таможенную стоимость */
  freightAfterBorderAmount?: number;
  freightCurrencyCode?: string;
  /** AI-обоснование оценки: notes + постатейная расшифровка того, из чего складывается сумма */
  freightJustification?: FreightJustification;

  // ─── Агрегированные итоги по всем товарам ──────────────────────────────
  totalProductCostKzt?: number;
  totalCustomsFees?: number;
  totalCustomsDuty?: number;
  totalVat?: number;
  totalExcise?: number;
  totalAntiDumpingDuty?: number;
  totalCustomsPayments?: number;
  allProductsClassified?: boolean;
  hasPendingFollowUpQuestions?: boolean;
  hasRestrictedProducts?: boolean;
  hasExciseExcludedProducts?: boolean;

  /** Полные данные PDF-инвойса. Null для ручных расчётов. */
  invoiceData?: InvoiceData;

  /** Оригинальный PDF, сохранённый в MinIO. */
  invoiceFile?: FileDto;

  invoiceProcessingStatus?: string;
  invoiceProcessingMessage?: string;
  invoiceProcessingCurrent?: number;
  invoiceProcessingTotal?: number;

  /** Уже посчитанные чеклисты документов по уникальным кодам ТНВЭД товаров расчёта.
   *  Код отсутствует в списке, если по нему проверка ещё не выполнялась. */
  certificateChecklists?: CertificateChecklist[];
  certificatesProcessingStatus?: string;
  certificatesProcessingMessage?: string;
  certificatesProcessingCurrent?: number;
  certificatesProcessingTotal?: number;
}

export interface InvoiceData {
  invoiceNumber?: string;
  invoiceDate?: string;
  sellerName?: string;
  sellerAddress?: string;
  sellerPhone?: string;
  sellerEmail?: string;
  sellerTaxId?: string;
  buyerName?: string;
  buyerAddress?: string;
  buyerPhone?: string;
  buyerEmail?: string;
  buyerTaxId?: string;
  paymentTerms?: string;
  countryOfOrigin?: string;
  /** Код Incoterms 2020, если явно указан в тексте инвойса */
  incotermCode?: string;
  currency?: string;
  totalAmount?: number;
  items?: InvoiceItem[];
}

export interface InvoiceItem {
  lineNumber?: number;
  nameOriginal?: string;
  quantity?: number;
  unit?: string;
  unitPrice?: number;
  productCost?: number;
  currencyCode?: string;
  tnvedCode?: string;
  applicationScope?: string;
}

export interface UpdateInvoiceItemRequest {
  productId: number;
  productCost?: number;
  currencyCode?: string;
  tnvedCode?: string;
  /** Исправления пользователя; отправляются, только если их меняли. Бэкенд должен сохранить их
   *  в товаре (nameOriginal) и в соответствующей позиции invoiceData.items. */
  nameOriginal?: string;
  unit?: string;
  quantity?: number;
  unitPrice?: number;
}

export interface UpdateInvoiceRequest {
  items: UpdateInvoiceItemRequest[];
  /** Реквизиты инвойса, исправленные пользователем. Отправляются, только если их меняли;
   *  бэкенд должен сохранить их в invoiceData (пустая строка — очистить поле). */
  invoiceNumber?: string;
  invoiceDate?: string;
  sellerName?: string;
  sellerAddress?: string;
  buyerName?: string;
  buyerAddress?: string;
  paymentTerms?: string;
}

export interface InvoiceProcessingStatus {
  calculationId: number;
  status: 'PROCESSING' | 'DONE' | 'ERROR' | string;
  stage?: string;
  message?: string;
  current?: number;
  total?: number;
  error?: string;
}

export interface TnvedSearchResult {
  code: string;
  name?: string;
  is10?: boolean;
}

export interface UpdateShipmentInfoRequest {
  fromCountryId?: number;
  fromCity?: string;
  toCountryId?: number;
  toCity?: string;
  incotermCode?: string;
  totalWeightKg?: number;
  totalVolumeM3?: number;
  transportType?: 'FTL' | 'LTL' | 'AIR' | 'CONTAINER';
  freightEnabled?: boolean;
  freightAmount?: number;
  freightAfterBorderAmount?: number;
  freightCurrencyCode?: string;
}

export interface DeliveryCostEstimate {
  beforeBorderAmountUsd?: number;
  afterBorderAmountUsd?: number;
  notes?: string;
  breakdown?: FreightBreakdownItem[];
}

export interface FreightBreakdownItem {
  label: string;
  amountUsd?: number;
}

export interface FreightJustification {
  notes?: string;
  breakdown?: FreightBreakdownItem[];
}

export interface CalculateCustomsWithAiRequest {
  applicationScope: string;
}

export interface UpdateCustomsCalculationBrokerRequest {
  brokerComment?: string;
}

export interface UpdateCustomsProductBrokerRequest {
  nameForBroker?: string;
  productCost?: number;
  tnvedCodeForBroker?: string;
  customsFees?: number;
  customsDuty?: number;
  vat?: number;
  totalCustomsPayments?: number;
}

@Injectable({
  providedIn: 'root'
})
export class CustomsCalculationsService {
  constructor(private http: HttpClient) {}

  /**
   * Создать новый независимый расчет таможенных платежей
   */
  createCalculation(request: CreateCustomsCalculationRequest): Observable<CustomsCalculation> {
    return this.http.post<CustomsCalculation>(API_URL, request);
  }

  /**
   * Создать расчет из PDF инвойса (извлечение товаров через AI на бэке)
   */
  createCalculationFromInvoice(file: File, title?: string): Observable<CustomsCalculation> {
    const formData = new FormData();
    formData.append('file', file);
    if (title != null && title.trim().length > 0) {
      formData.append('title', title.trim());
    }
    return this.http.post<CustomsCalculation>(INVOICE_API_URL, formData);
  }

  /**
   * Поиск независимых расчетов с фильтрацией и пагинацией (только текущего пользователя)
   */
  searchCalculations(request: SearchCustomsCalculationsRequest, page: number = 0, size: number = 20): Observable<PageResponse<CustomsCalculation>> {
    let httpParams = new HttpParams();
    httpParams = httpParams.set('page', page.toString());
    httpParams = httpParams.set('size', size.toString());
    httpParams = httpParams.set('sort', 'createdAt,desc');

    return this.http.post<PageResponse<CustomsCalculation>>(`${API_URL}/search`, request, { params: httpParams });
  }

  /**
   * Поиск всех независимых расчетов (для таможенного брокера — расчеты всех пользователей)
   */
  searchCalculationsForBroker(request: SearchCustomsCalculationsRequest, page: number = 0, size: number = 20): Observable<PageResponse<CustomsCalculation>> {
    let httpParams = new HttpParams();
    httpParams = httpParams.set('page', page.toString());
    httpParams = httpParams.set('size', size.toString());
    httpParams = httpParams.set('sort', 'createdAt,desc');

    return this.http.post<PageResponse<CustomsCalculation>>(`${API_URL}/search-for-broker`, request, { params: httpParams });
  }

  /**
   * Получить расчет по ID
   */
  getCalculationById(calculationId: number): Observable<CustomsCalculation> {
    return this.http.get<CustomsCalculation>(`${API_URL}/${calculationId}`);
  }

  getInvoiceProcessingStatus(calculationId: number): Observable<InvoiceProcessingStatus> {
    return this.http.get<InvoiceProcessingStatus>(`${API_URL}/${calculationId}/invoice-status`);
  }

  /**
   * Удалить расчет
   */
  deleteCalculation(calculationId: number): Observable<void> {
    return this.http.delete<void>(`${API_URL}/${calculationId}`);
  }

  updateCalculationByBroker(calculationId: number, request: UpdateCustomsCalculationBrokerRequest): Observable<CustomsCalculation> {
    return this.http.put<CustomsCalculation>(`${API_URL}/${calculationId}/broker`, request);
  }

  /**
   * Обновить позиции инвойса (суммы / коды ТНВЭД) и пересчитать платежи
   */
  updateInvoiceItems(calculationId: number, request: UpdateInvoiceRequest): Observable<CustomsCalculation> {
    return this.http.patch<CustomsCalculation>(`${API_URL}/${calculationId}/invoice`, request);
  }

  /**
   * Поиск кодов ТНВЭД по префиксу (минимум 2 символа)
   */
  searchTnved(prefix: string): Observable<TnvedSearchResult[]> {
    const params = new HttpParams().set('prefix', prefix);
    return this.http.get<TnvedSearchResult[]>('/cargo/open-api/dic/tnved/search', { params });
  }

  /**
   * Сохранить информацию о поставке (страны/города, Incoterms, страховка, фрахт)
   * и пересчитать итоги по всем товарам расчёта
   */
  updateShipmentInfo(calculationId: number, request: UpdateShipmentInfoRequest): Observable<CustomsCalculation> {
    return this.http.put<CustomsCalculation>(`${API_URL}/${calculationId}/shipment-info`, request);
  }

  /** Ответ «Товары доставит в вашу страну продавец?»: да — доставку не добавляем к таможенной стоимости,
   *  нет — добавляем 80% (авиа 100%). Бэкенд пересчитывает таможенную стоимость и платежи. */
  setSellerDelivers(calculationId: number, sellerDelivers: boolean): Observable<CustomsCalculation> {
    return this.http.put<CustomsCalculation>(`${API_URL}/${calculationId}/seller-delivers`, { sellerDelivers });
  }

  /** Отметить начало обработки расчёта (CREATED → IN_PROCESSING). Идемпотентно. */
  startProcessing(calculationId: number): Observable<CustomsCalculation> {
    return this.http.post<CustomsCalculation>(`${API_URL}/${calculationId}/start-processing`, {});
  }

  /**
   * AI-оценка стоимости доставки (до границы/аэропорта и после) по уже сохранённым
   * маршруту, весу, объёму и типу перевозки. Результат сразу сохраняется и учитывается
   * в пересчёте таможенной стоимости.
   */
  estimateDeliveryCost(calculationId: number): Observable<CustomsCalculation> {
    return this.http.post<CustomsCalculation>(`${API_URL}/${calculationId}/estimate-delivery-cost`, {});
  }

  /**
   * Сгенерировать (или переиспользовать уже существующую) ссылку-приглашение поставщика
   * на эту перевозку (вкладка «Поставка»). Доступно только владельцу расчёта.
   */
  generateSupplierInvite(calculationId: number): Observable<{ token: string; calculationId: number }> {
    return this.http.post<{ token: string; calculationId: number }>(
      `${API_URL}/${calculationId}/supplier-invite`,
      {}
    );
  }

  /** Расчёты, к которым привязан текущий поставщик (вкладка «Поставщик» в его кабинете). */
  getMyCalculationsAsSupplier(): Observable<CustomsCalculation[]> {
    return this.http.get<CustomsCalculation[]>(`${API_URL}/supplier/my`);
  }

  /**
   * Принять приглашение уже зарегистрированным (авторизованным) поставщиком — без
   * повторной регистрации, только привязка к перевозке.
   */
  acceptSupplierInvite(token: string): Observable<CustomsCalculation> {
    return this.http.post<CustomsCalculation>(`${API_URL}/supplier-invite/${token}/accept`, {});
  }

  /**
   * Добавить товар в расчет
   */
  addProduct(calculationId: number, request: CreateCustomsProductRequest): Observable<CustomsProduct> {
    return this.http.post<CustomsProduct>(`${API_URL}/${calculationId}/products`, request);
  }

  updateProductByBroker(calculationId: number, productId: number, request: UpdateCustomsProductBrokerRequest): Observable<CustomsProduct> {
    return this.http.put<CustomsProduct>(`${API_URL}/${calculationId}/products/${productId}/broker`, request);
  }

  deleteProductByBroker(calculationId: number, productId: number): Observable<void> {
    return this.http.delete<void>(`${API_URL}/${calculationId}/products/${productId}/broker`);
  }

  /**
   * Рассчитать таможенные платежи через AI
   */
  calculateWithAi(calculationId: number, productId: number, request: CalculateCustomsWithAiRequest): Observable<CustomsProduct> {
    return this.http.post<CustomsProduct>(
      `${API_URL}/${calculationId}/products/${productId}/calculate-with-ai`,
      request
    );
  }

  /**
   * Предварительный расчёт (шаг 2) — считает, но НЕ сохраняет в БД.
   * Можно вызывать многократно с разными вариантами ответов.
   */
  previewRefineCalculation(calculationId: number, productId: number, request: RefineCalculationRequest): Observable<CustomsProduct> {
    return this.http.post<CustomsProduct>(
      `${API_URL}/${calculationId}/products/${productId}/refine-preview`,
      request
    );
  }

  /**
   * Уточнённый расчёт (шаг 2) — считает И сохраняет в БД
   */
  refineCalculation(calculationId: number, productId: number, request: RefineCalculationRequest): Observable<CustomsProduct> {
    return this.http.post<CustomsProduct>(
      `${API_URL}/${calculationId}/products/${productId}/refine`,
      request
    );
  }

  /**
   * Запустить асинхронную проверку разрешительных документов по всем уникальным кодам ТНВЭД
   * расчёта. Прогресс — через ChatSocketService-подобный сокет на /topic/certificates/{id}.
   */
  startCertificatesCheck(calculationId: number): Observable<CustomsCalculation> {
    return this.http.post<CustomsCalculation>(`${API_URL}/${calculationId}/certificates/check`, {});
  }

  /**
   * Poll-эндпоинт статуса проверки документов (дублирует WebSocket).
   */
  getCertificatesProcessingStatus(calculationId: number): Observable<CertificatesProcessingStatus> {
    return this.http.get<CertificatesProcessingStatus>(`${API_URL}/${calculationId}/certificates-status`);
  }
}

export interface CertificatesProcessingStatus {
  calculationId: number;
  /** PROCESSING | DONE | ERROR */
  status: string;
  message?: string;
  current?: number;
  total?: number;
  error?: string;
}
